import { useReducer } from "react";

// Initial state
const initialState = {
  postType: "",
  title: "",
  content: "",
  tags: [],
  currentTag: "",
  loading: false,
  backgroundOption: "white",
  selectedFont: "font-georgia",
  selectedColor: "black",
  loadedDraftId: null,

  // Chapter-based content (Novel, Book, Story) - Updated to 10 chapters
  chapters: [],
  currentChapter: 1,
  chapterTitles: ["", "", "", "", "", "", "", "", "", ""], 
  chapterContents: ["", "", "", "", "", "", "", "", "", ""], 

  // Scene-based content (Script) - 10 scenes
  scenes: [],
  currentScene: 1,
  sceneTitles: ["", "", "", "", "", "", "", "", "", ""], 
  sceneContents: ["", "", "", "", "", "", "", "", "", ""], 
};

// Action types
export const POST_ACTIONS = {
  SET_POST_TYPE: "SET_POST_TYPE",
  SET_TITLE: "SET_TITLE",
  SET_CONTENT: "SET_CONTENT",
  ADD_TAG: "ADD_TAG",
  REMOVE_TAG: "REMOVE_TAG",
  SET_CURRENT_TAG: "SET_CURRENT_TAG",
  SET_LOADING: "SET_LOADING",
  SET_BACKGROUND_OPTION: "SET_BACKGROUND_OPTION",
  SET_SELECTED_FONT: "SET_SELECTED_FONT",
  SET_SELECTED_COLOR: "SET_SELECTED_COLOR",
  RESET_FORM: "RESET_FORM",
  APPLY_TEXT_FORMATTING: "APPLY_TEXT_FORMATTING",
  INSERT_EMOJI: "INSERT_EMOJI",
  LOAD_DRAFT: "LOAD_DRAFT",

  // Chapter-based actions (Novel, Book, Story)
  SET_CURRENT_CHAPTER: "SET_CURRENT_CHAPTER",
  SET_CHAPTER_TITLE: "SET_CHAPTER_TITLE",
  SET_CHAPTER_CONTENT: "SET_CHAPTER_CONTENT",
  ADD_CHAPTER: "ADD_CHAPTER",
  REMOVE_CHAPTER: "REMOVE_CHAPTER",

  // Scene-based actions (Script)
  SET_CURRENT_SCENE: "SET_CURRENT_SCENE",
  SET_SCENE_TITLE: "SET_SCENE_TITLE",
  SET_SCENE_CONTENT: "SET_SCENE_CONTENT",
  ADD_SCENE: "ADD_SCENE",
  REMOVE_SCENE: "REMOVE_SCENE",
};

// Helper function to validate and format content
const validateAndFormatContent = (content) => {
  const lines = content.split("\n");
  const processedLines = [];

  lines.forEach((line) => {
    if (line.length <= 42) {
      processedLines.push(line);
    } else {
      const chunks = [];
      for (let i = 0; i < line.length; i += 42) {
        chunks.push(line.substring(i, i + 42));
      }
      processedLines.push(...chunks);
    }
  });

  let result = processedLines.join("\n");
  result = result.replace(/\n{3,}/g, "\n\n");

  return result;
};

const isChapterType = (postType) => ["Novel", "Book", "Story"].includes(postType);
const isSceneType = (postType) => ["Script"].includes(postType);

// Reducer function
const postReducer = (state, action) => {
  switch (action.type) {
    case POST_ACTIONS.SET_POST_TYPE:
      // const isChapter = isChapterType(action.payload);
      // const isScene = isSceneType(action.payload);
      // return {
      //   ...state,
      //   postType: action.payload,
      //   content: isChapter || isScene ? "" : state.content,
      //   chapters: isChapter ? [] : [],
      //   currentChapter: isChapter ? 1 : state.currentChapter,
      //   scenes: isScene ? [] : [],
      //   currentScene: isScene ? 1 : state.currentScene,
      // };
      return {
        ...initialState,
        postType: action.payload,
      };

    case POST_ACTIONS.SET_TITLE:
      return { ...state, title: action.payload };

    case POST_ACTIONS.SET_CONTENT:
      const isChapterTypeContent = isChapterType(state.postType);
      const isSceneTypeContent = isSceneType(state.postType);
      return {
        ...state,
        content: isChapterTypeContent || isSceneTypeContent
          ? action.payload
          : validateAndFormatContent(action.payload),
      };

    case POST_ACTIONS.ADD_TAG:
      if (state.tags.includes(action.payload) || state.tags.length >= 5) {
        return state;
      }
      return {
        ...state,
        tags: [...state.tags, action.payload],
        currentTag: "",
      };

    case POST_ACTIONS.REMOVE_TAG:
      return {
        ...state,
        tags: state.tags.filter((tag) => tag !== action.payload),
      };

    case POST_ACTIONS.SET_CURRENT_TAG:
      return { ...state, currentTag: action.payload };

    case POST_ACTIONS.SET_LOADING:
      return { ...state, loading: action.payload };

    case POST_ACTIONS.SET_BACKGROUND_OPTION:
      return { ...state, backgroundOption: action.payload };

    case POST_ACTIONS.SET_SELECTED_FONT:
      return { ...state, selectedFont: action.payload };

    case POST_ACTIONS.SET_SELECTED_COLOR:
      return { ...state, selectedColor: action.payload };

    case POST_ACTIONS.APPLY_TEXT_FORMATTING:
      const isChapterTypeFormat = isChapterType(state.postType);
      const isSceneTypeFormat = isSceneType(state.postType);
      return {
        ...state,
        content: isChapterTypeFormat || isSceneTypeFormat
          ? action.payload.content
          : validateAndFormatContent(action.payload.content),
      };

    case POST_ACTIONS.INSERT_EMOJI:
      const isChapterTypeEmoji = isChapterType(state.postType);
      const isSceneTypeEmoji = isSceneType(state.postType);
      return {
        ...state,
        content: isChapterTypeEmoji || isSceneTypeEmoji
          ? action.payload.content
          : validateAndFormatContent(action.payload.content),
      };

    case POST_ACTIONS.SET_CURRENT_CHAPTER:
      return { ...state, currentChapter: action.payload };

    case POST_ACTIONS.SET_CHAPTER_TITLE:
      const newTitles = [...state.chapterTitles];
      newTitles[action.payload.index] = action.payload.title;
      return { ...state, chapterTitles: newTitles };

    case POST_ACTIONS.SET_CHAPTER_CONTENT:
      const newContents = [...state.chapterContents];
      newContents[action.payload.index] = action.payload.content;
      return { ...state, chapterContents: newContents };

    case POST_ACTIONS.ADD_CHAPTER:
      if (state.chapters.length >= 10) return state;
      return { ...state, chapters: [...state.chapters, action.payload] };

    case POST_ACTIONS.REMOVE_CHAPTER:
      const updatedChapters = state.chapters.filter((_, idx) => idx !== action.payload);
      const updatedTitles = [...state.chapterTitles];
      const updatedContents = [...state.chapterContents];
      updatedTitles[action.payload] = "";
      updatedContents[action.payload] = "";
      return {
        ...state,
        chapters: updatedChapters,
        chapterTitles: updatedTitles,
        chapterContents: updatedContents,
        currentChapter: Math.min(state.currentChapter, updatedChapters.length || 1),
      };

    case POST_ACTIONS.SET_CURRENT_SCENE:
      return { ...state, currentScene: action.payload };

    case POST_ACTIONS.SET_SCENE_TITLE:
      const newSceneTitles = [...state.sceneTitles];
      newSceneTitles[action.payload.index] = action.payload.title;
      return { ...state, sceneTitles: newSceneTitles };

    case POST_ACTIONS.SET_SCENE_CONTENT:
      const newSceneContents = [...state.sceneContents];
      newSceneContents[action.payload.index] = action.payload.content;
      return { ...state, sceneContents: newSceneContents };

    case POST_ACTIONS.ADD_SCENE:
      if (state.scenes.length >= 10) return state;
      return { ...state, scenes: [...state.scenes, action.payload] };

    case POST_ACTIONS.REMOVE_SCENE:
      const updatedScenes = state.scenes.filter((_, idx) => idx !== action.payload);
      const updatedSceneTitles = [...state.sceneTitles];
      const updatedSceneContents = [...state.sceneContents];
      updatedSceneTitles[action.payload] = "";
      updatedSceneContents[action.payload] = "";
      return {
        ...state,
        scenes: updatedScenes,
        sceneTitles: updatedSceneTitles,
        sceneContents: updatedSceneContents,
        currentScene: Math.min(state.currentScene, updatedScenes.length || 1),
      };

    case POST_ACTIONS.RESET_FORM:
      return { ...initialState };

    case POST_ACTIONS.LOAD_DRAFT: {
      const draft = action.payload;
      const isChapter = isChapterType(draft.type);
      const isScene = isSceneType(draft.type);

      const chapterTitles = ["", "", "", "", "", "", "", "", "", ""];
      const chapterContents = ["", "", "", "", "", "", "", "", "", ""];
      if (isChapter && Array.isArray(draft.chapters)) {
        draft.chapters.forEach((ch) => {
          const idx = (ch.chapterNumber || 1) - 1;
          if (idx >= 0 && idx < 10) {
            chapterTitles[idx] = ch.title || "";
            chapterContents[idx] = ch.content || "";
          }
        });
      }

      const sceneTitles = ["", "", "", "", "", "", "", "", "", ""];
      const sceneContents = ["", "", "", "", "", "", "", "", "", ""];
      if (isScene && Array.isArray(draft.scenes)) {
        draft.scenes.forEach((sc) => {
          const idx = (sc.sceneNumber || 1) - 1;
          if (idx >= 0 && idx < 10) {
            sceneTitles[idx] = sc.title || "";
            sceneContents[idx] = sc.content || "";
          }
        });
      }

      return {
        ...initialState,
        postType: draft.type || "",
        title: draft.title || "",
        content: isChapter || isScene ? "" : draft.content || "",
        tags: draft.hashtags || [],
        backgroundOption: draft.styling?.background || "white",
        selectedFont: draft.styling?.font || "font-georgia",
        selectedColor: draft.styling?.color || "black",
        chapterTitles,
        chapterContents,
        currentChapter: 1,
        sceneTitles,
        sceneContents,
        currentScene: 1,
        loadedDraftId: draft._id,
      };
    }

    default:
      return state;
  }
};

export const usePostReducer = () => {
  const [state, dispatch] = useReducer(postReducer, initialState);

  const actions = {
    setPostType: (postType) => dispatch({ type: POST_ACTIONS.SET_POST_TYPE, payload: postType }),
    setTitle: (title) => dispatch({ type: POST_ACTIONS.SET_TITLE, payload: title }),
    setContent: (content) => dispatch({ type: POST_ACTIONS.SET_CONTENT, payload: content }),
    addTag: (tag) => dispatch({ type: POST_ACTIONS.ADD_TAG, payload: tag.trim() }),
    removeTag: (tag) => dispatch({ type: POST_ACTIONS.REMOVE_TAG, payload: tag }),
    setCurrentTag: (currentTag) => dispatch({ type: POST_ACTIONS.SET_CURRENT_TAG, payload: currentTag }),
    setLoading: (loading) => dispatch({ type: POST_ACTIONS.SET_LOADING, payload: loading }),
    setBackgroundOption: (backgroundOption) => dispatch({ type: POST_ACTIONS.SET_BACKGROUND_OPTION, payload: backgroundOption }),
    setSelectedFont: (selectedFont) => dispatch({ type: POST_ACTIONS.SET_SELECTED_FONT, payload: selectedFont }),
    setSelectedColor: (selectedColor) => dispatch({ type: POST_ACTIONS.SET_SELECTED_COLOR, payload: selectedColor }),
    applyTextFormatting: (content) => dispatch({ type: POST_ACTIONS.APPLY_TEXT_FORMATTING, payload: { content } }),
    insertEmoji: (content) => dispatch({ type: POST_ACTIONS.INSERT_EMOJI, payload: { content } }),
    resetForm: () => dispatch({ type: POST_ACTIONS.RESET_FORM }),
    loadDraft: (draft) => dispatch({ type: POST_ACTIONS.LOAD_DRAFT, payload: draft }),

    // Chapter-based actions
    setCurrentChapter: (chapterNum) => dispatch({ type: POST_ACTIONS.SET_CURRENT_CHAPTER, payload: chapterNum }),
    setChapterTitle: (index, title) => dispatch({ type: POST_ACTIONS.SET_CHAPTER_TITLE, payload: { index, title } }),
    setChapterContent: (index, content) => dispatch({ type: POST_ACTIONS.SET_CHAPTER_CONTENT, payload: { index, content } }),
    addChapter: (chapter) => dispatch({ type: POST_ACTIONS.ADD_CHAPTER, payload: chapter }),
    removeChapter: (index) => dispatch({ type: POST_ACTIONS.REMOVE_CHAPTER, payload: index }),

    // Scene-based actions
    setCurrentScene: (sceneNum) => dispatch({ type: POST_ACTIONS.SET_CURRENT_SCENE, payload: sceneNum }),
    setSceneTitle: (index, title) => dispatch({ type: POST_ACTIONS.SET_SCENE_TITLE, payload: { index, title } }),
    setSceneContent: (index, content) => dispatch({ type: POST_ACTIONS.SET_SCENE_CONTENT, payload: { index, content } }),
    addScene: (scene) => dispatch({ type: POST_ACTIONS.ADD_SCENE, payload: scene }),
    removeScene: (index) => dispatch({ type: POST_ACTIONS.REMOVE_SCENE, payload: index }),
  };

  const helpers = {
    handleTagKeyDown: (e) => {
      if (e.key === "Enter" && state.currentTag.trim()) {
        e.preventDefault();
        actions.addTag(state.currentTag);
      }
    },
    handleContentKeyDown: (e) => {
      const isChapter = ["Novel", "Book", "Story"].includes(state.postType);
      const isScene = ["Script"].includes(state.postType);
      if (isChapter || isScene) return;

      const textarea = e.target;
      const { value, selectionStart } = textarea;

      if (e.key === "Enter") {
        const beforeCursor = value.substring(0, selectionStart);
        const afterCursor = value.substring(selectionStart);
        const beforeNewlines = beforeCursor.match(/\n*$/)?.[0] || "";
        const afterNewlines = afterCursor.match(/^\n*/)?.[0] || "";

        if (beforeNewlines.length + afterNewlines.length >= 2) {
          e.preventDefault();
        }
      }
    },
    getCurrentLineInfo: (content, cursorPosition) => {
      const isChapter = ["Novel", "Book", "Story"].includes(state.postType);
      const isScene = ["Script"].includes(state.postType);
      if (isChapter || isScene) {
        return { currentLine: "", lineLength: 0, maxLength: 5000 };
      }

      const beforeCursor = content.substring(0, cursorPosition);
      const currentLineStart = beforeCursor.lastIndexOf("\n") + 1;
      const currentLine = content.substring(currentLineStart).split("\n")[0];
      return {
        currentLine,
        lineLength: currentLine.length,
        maxLength: 42,
      };
    },
  };

  return { state, actions, helpers };
};